import React, { useState, useEffect, useCallback } from "react";
import {
  Copy24Regular,
  Dismiss24Regular,
  Share24Regular,
  LinkDismiss24Regular,
} from "@fluentui/react-icons";
import {
  historyShare,
  historyGetShare,
  historyRevokeShare,
  ConversationShareInfo,
} from "../../../api";
import "./ShareDialog.css";

interface ShareDialogProps {
  isOpen: boolean;
  conversationId: string;
  conversationTitle?: string;
  onDismiss: () => void;
}

export const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  conversationId,
  conversationTitle,
  onDismiss,
}) => {
  const [activeShare, setActiveShare] = useState<ConversationShareInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const loadShare = useCallback(async () => {
    if (!conversationId) return;
    setLoading(true);
    const result = await historyGetShare(conversationId);
    setActiveShare(result);
    setLoading(false);
  }, [conversationId]);

  useEffect(() => {
    if (isOpen) {
      loadShare();
      setCopied(false);
    }
  }, [isOpen, loadShare]);

  const handleCreateShare = async () => {
    setCreating(true);
    const result = await historyShare(conversationId);
    if (result) {
      await loadShare();
      // Auto-copy the new link
      const shareUrl = `${window.location.origin}/share/${result.share_token}`;
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
    setCreating(false);
  };

  const handleCopyLink = async () => {
    if (!activeShare) return;
    const shareUrl = `${window.location.origin}/share/${activeShare.share_token}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    setRevoking(true);
    const success = await historyRevokeShare(conversationId);
    if (success) {
      setActiveShare(null);
    }
    setRevoking(false);
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    },
    [onDismiss]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div className="share-dialog-backdrop" onClick={onDismiss} role="dialog" aria-modal="true">
      <div className="share-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="share-dialog-header">
          <div className="share-dialog-header-icon">
            <Share24Regular />
          </div>
          <h3 className="share-dialog-title">
            Share{conversationTitle ? ` "${conversationTitle}"` : " Conversation"}
          </h3>
          <button className="share-dialog-close" onClick={onDismiss} title="Close">
            <Dismiss24Regular />
          </button>
        </div>

        <div className="share-dialog-body">
          {loading && <div className="share-dialog-loading">Loading...</div>}

          {!loading && !activeShare && (
            <button
              className="share-dialog-create-btn"
              onClick={handleCreateShare}
              disabled={creating}
            >
              <Share24Regular />
              {creating ? "Creating..." : "Generate link"}
            </button>
          )}

          {!loading && activeShare && (
            <div className="share-dialog-section">
              <h4 className="share-dialog-section-title">Share link</h4>
              <div className="share-link-item">
                <div className="share-link-info">
                  <span className="share-link-url">
                    {window.location.origin}/share/{activeShare.share_token.substring(0, 20)}...
                  </span>
                  <span className="share-link-date">
                    {new Date(activeShare.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="share-link-actions">
                  <button
                    className="share-link-btn"
                    onClick={handleCopyLink}
                    title="Copy link"
                  >
                    <Copy24Regular />
                    {copied ? "Copied!" : "Copy"}
                  </button>
                  <button
                    className="share-link-btn share-link-btn-revoke"
                    onClick={handleRevoke}
                    disabled={revoking}
                    title="Revoke link"
                  >
                    <LinkDismiss24Regular />
                    {revoking ? "Revoking..." : "Revoke"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
